import { useAtomSet, useAtomValue } from '@effect/atom-react';
import ListeningLogo from '@listening/assets/astro/icons/new-design-25/compare/listening-logo.svg?react';
import { cn } from '@listening/utils/react-helpers';
import { Option } from 'effect';
import { Atom } from 'effect/unstable/reactivity';
import {
  BookmarkBook,
  Import,
  Infinite,
  List,
  MailIn,
  Scissor,
  SmartphoneDevice,
  SoundHigh,
  Sparks,
} from 'iconoir-react';
import type { ComponentType } from 'react';
import * as m from '@/paraglide/messages';
import { SectionHeading } from './section-heading';

const ADVANTAGES: ReadonlyArray<{
  Icon: ComponentType<{ className?: string }>;
  label: () => string;
}> = [
  { Icon: Scissor, label: m.why_listening_advantage_citations },
  { Icon: List, label: m.why_listening_advantage_sections },
  { Icon: Import, label: m.why_listening_advantage_zotero },
  { Icon: MailIn, label: m.why_listening_advantage_newsletters },
  { Icon: Infinite, label: m.why_listening_advantage_quotas },
  { Icon: SoundHigh, label: m.why_listening_advantage_pronunciation },
  { Icon: Sparks, label: m.why_listening_advantage_insights },
  { Icon: BookmarkBook, label: m.why_listening_advantage_notes },
  { Icon: SmartphoneDevice, label: m.why_listening_advantage_devices },
];

const FLIP_EVERY_MS = 2500;

const FLIP_ORDER = [0, 2, 1, 3];

type Card = { front: number; back: number; flipped: boolean };

type Deck = { cards: ReadonlyArray<Card>; next: number; turn: number };

const initialDeck: Deck = {
  cards: [0, 1, 2, 3].map((index) => ({
    front: index,
    back: index,
    flipped: false,
  })),
  next: 4,
  turn: 0,
};

function flipNext(deck: Deck): Deck {
  const slot = FLIP_ORDER[deck.turn % FLIP_ORDER.length] ?? 0;
  return {
    cards: deck.cards.map((card, index) => {
      if (index !== slot) return card;
      return card.flipped
        ? { ...card, front: deck.next, flipped: false }
        : { ...card, back: deck.next, flipped: true };
    }),
    next: (deck.next + 1) % ADVANTAGES.length,
    turn: deck.turn + 1,
  };
}

const pausedAtom = Atom.make(false);

const deckAtom = Atom.make((get) => {
  const deck = Option.getOrElse(get.self<Deck>(), () => initialDeck);
  if (
    get(pausedAtom) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return deck;
  }
  const timer = setInterval(() => {
    get.setSelf(
      flipNext(Option.getOrElse(get.self<Deck>(), () => initialDeck)),
    );
  }, FLIP_EVERY_MS);
  get.addFinalizer(() => clearInterval(timer));
  return deck;
}).pipe(Atom.withServerValue(() => initialDeck));

function CardFace({
  advantage,
  reverse,
  hidden,
}: {
  advantage: number;
  reverse: boolean;
  hidden: boolean;
}) {
  const { Icon, label } = ADVANTAGES[advantage] ?? ADVANTAGES[0];
  return (
    <div
      aria-hidden={hidden}
      className={cn(
        'absolute inset-0 flex flex-col justify-between rounded-2xl bg-(--v2-app-bg) p-4 text-[15px] leading-snug font-medium tracking-[-0.01em] text-(--v2-text-primary) ring-1 ring-black/[0.06] backface-hidden sm:p-3 sm:text-[13px] lg:p-5 lg:text-base',
        reverse && 'rotate-y-180',
      )}
    >
      <Icon aria-hidden='true' className='size-5 text-azure-595' />
      {label()}
    </div>
  );
}

function AdvantageCard({ card, className }: { card: Card; className: string }) {
  return (
    <li className={cn('perspective-distant', className)}>
      <div
        className={cn(
          'relative size-full transition-transform duration-700 ease-in-out transform-3d motion-reduce:transition-none',
          card.flipped && 'rotate-y-180',
        )}
      >
        <CardFace
          advantage={card.front}
          hidden={card.flipped}
          reverse={false}
        />
        <CardFace advantage={card.back} hidden={!card.flipped} reverse />
      </div>
    </li>
  );
}

export function AdvantagesSection() {
  const { cards } = useAtomValue(deckAtom);
  const setPaused = useAtomSet(pausedAtom);
  const [first, second, third, fourth] = cards;

  return (
    <section
      aria-labelledby='why-advantages-heading'
      className='overflow-x-clip px-5 py-14 sm:px-6 sm:py-24 lg:py-28'
    >
      <SectionHeading
        description={m.why_listening_advantages_description()}
        id='why-advantages-heading'
        title={m.why_listening_advantages_title()}
      />
      <div
        className='mx-auto mt-10 max-w-5xl sm:mt-14'
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <ul className='grid grid-cols-2 gap-2.5 sm:grid-cols-5 sm:gap-3'>
          {first && (
            <AdvantageCard
              card={first}
              className='aspect-[5/4] sm:aspect-square'
            />
          )}
          {second && (
            <AdvantageCard
              card={second}
              className='aspect-[5/4] sm:aspect-square'
            />
          )}
          {third && (
            <AdvantageCard
              card={third}
              className='aspect-[5/4] sm:aspect-square'
            />
          )}
          {fourth && (
            <AdvantageCard
              card={fourth}
              className='aspect-[5/4] sm:aspect-square'
            />
          )}
          <li className='col-span-2 flex aspect-[5/2] flex-col justify-between rounded-2xl bg-(--v2-accent) p-4 text-lg leading-snug font-semibold tracking-[-0.02em] text-white shadow-[0_12px_32px_-12px_rgb(5_135_251/0.6)] sm:col-span-1 sm:aspect-square sm:p-3 sm:text-sm lg:p-5 lg:text-xl'>
            <ListeningLogo
              aria-hidden='true'
              className='h-6 w-auto self-start'
            />
            {m.why_listening_advantage_one_app()}
          </li>
        </ul>
      </div>
    </section>
  );
}
